import { CSSProperties } from 'react';
import { getReferenceString } from '@medplum/core';
import { rememberOpenedPatient } from '../services/queueHydrationCache';

/**
 * Rows grow from 36px to 42px to fit a 24px avatar — the smallest canon size —
 * applied uniformly within a tab rather than special-casing the rows that happen
 * to have a person on them.
 *
 * Opt-in on purpose. The CI tabs ask for it; the six existing tabs keep their
 * current spacing until they have avatars to fit, because re-spacing every
 * shipped tab before a single avatar exists on any of them is a visible change
 * that buys nothing.
 */
export const LIST_ROW_HEIGHT_WITH_AVATAR = 42;

export interface ListRowStyleOptions {
  clickable: boolean;
  /** Omit to leave the table's own row height alone. */
  rowHeight?: number;
}

export function getListRowStyle({ clickable, rowHeight }: ListRowStyleOptions): CSSProperties {
  const cursor = clickable ? 'pointer' : 'default';

  // Both height and minHeight: mantine-react-table's cell padding otherwise wins
  // over a bare height on the row.
  return rowHeight === undefined ? { cursor } : { cursor, height: rowHeight, minHeight: rowHeight };
}

/** The subset of a CI queue row the row-props helper needs. */
export interface PacQueueRowLike {
  patient?: { id?: string };
}

/**
 * Row props shared by both CI queues.
 *
 * `original` is optional and every field off it is optional because MRT renders
 * SKELETON rows while a table is loading, and a skeleton row's `original` is
 * `{}`. Reading `original.patient.id` on one throws and takes the whole tab to
 * the error boundary — which shipped, twice, in two different callbacks.
 *
 * Extracted from the tabs so it can be called directly with a skeleton row in a
 * test. It cannot be covered by rendering: under jsdom the virtualizer measures
 * zero height, renders no body rows, and never invokes this at all.
 */
export function getCiQueueRowProps(
  original: PacQueueRowLike | undefined,
  onOpenPatient: (patientId: string) => void,
  // The Intake hub's QueueTable passes `{}`: its cell padding sets the height.
  { rowHeight }: Pick<ListRowStyleOptions, 'rowHeight'> = { rowHeight: LIST_ROW_HEIGHT_WITH_AVATAR }
): { onClick: () => void; style: CSSProperties } {
  const patientId = original?.patient?.id;

  return {
    onClick: () => {
      if (patientId) {
        // So the queue re-hydrates this row when the rep comes back (BAR-2786).
        rememberOpenedPatient(getReferenceString({ resourceType: 'Patient', id: patientId }));
        onOpenPatient(patientId);
      }
    },
    style: getListRowStyle({ clickable: Boolean(patientId), rowHeight }),
  };
}
